import { portalPaths } from '@provision/portal-routes';
import { useMemo } from 'react';
import {
  PortalioImportContext,
  type PortalioImportContextValue,
} from 'src/components/ImportPortalioProjectModal/portalio-import-context';
import { ProjectsSummary } from 'src/components/ImportPortalioProjectModal/projects-summary';
import { PortalioImportRows } from 'src/components/ImportProposalsModal/import-rows';
import { ImportSelectStep } from 'src/components/ImportProposalsModal/import-select-step';
import type { ImportListRow } from 'src/components/ImportProposalsModal/use-import-list-rows';
import { ModalContainer, useModalSteps } from 'src/components/ModalContainer';
import { useImportPortalioProject } from 'src/graphql/entities/integrations/portalio/hooks/use-import-portalio-project';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNavigateRoute } from 'src/utils/routing';

interface PortalioSelectStepProps {
  onCancel: () => void;
}

/** Step 1: the portal.io proposals to import as projects. */
const PortalioSelectStep = ({ onCancel }: PortalioSelectStepProps) => {
  const { pushStep } = useModalSteps();

  const handleReview = (selected: ImportListRow[]) => {
    const projects = selected.map(({ id, name }) => ({ id: Number(id), name }));
    void pushStep(<ProjectsSummary projects={projects} />);
  };

  return (
    <ImportSelectStep
      title="Import from Portal.io"
      Rows={PortalioImportRows}
      onReview={handleReview}
      onCancel={onCancel}
      testIdPrefix="import-portalio-projects"
    />
  );
};

/** Import portal.io proposals as projects: select, preview, import, see the results. */
export const ImportPortalioProjectModal = () => {
  const { isOpened } = useModalState(Modals.ImportPortalioProjects);
  const { closeModal } = useModalMethods(Modals.ImportPortalioProjects);
  const { navigate } = useNavigateRoute();
  const { importPortalioProject, loading, records, errors } = useImportPortalioProject();

  const context = useMemo<PortalioImportContextValue>(
    () => ({
      importProjects: importPortalioProject,
      importing: loading,
      records,
      errors,
      openSite: (addressId) => {
        closeModal();
        navigate(portalPaths.site.full(addressId));
      },
      close: closeModal,
    }),
    [closeModal, errors, importPortalioProject, loading, navigate, records],
  );

  return (
    <PortalioImportContext.Provider value={context}>
      <ModalContainer
        open={isOpened}
        size="large"
        onClose={closeModal}
        data-testid="import-portalio-projects-modal"
      >
        <PortalioSelectStep onCancel={closeModal} />
      </ModalContainer>
    </PortalioImportContext.Provider>
  );
};
